import React, { useRef, useState } from 'react';
import AuditMessage from './AuditMessage';

interface Props {
  acceptedExtensions: string[]; // ex.: ['pdf', 'ofx']
  multiple: boolean;
  disabled?: boolean;
  onFilesSelected: (files: File[]) => void;
}

const formatarTamanho = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Épico "Central de Entrada de Dados", seção 11 — upload por clique, drag and drop, múltiplos
// arquivos, exibição de nome/extensão/tamanho e remoção individual antes do envio.
const AuditFileUploader: React.FC<Props> = ({ acceptedExtensions, multiple, disabled, onFilesSelected }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);
  const [selecionados, setSelecionados] = useState<File[]>([]);
  const [erroFormato, setErroFormato] = useState<string | null>(null);

  const extensoesTexto = acceptedExtensions.map((e) => e.toUpperCase()).join(' e ');
  const acceptAttr = acceptedExtensions.map((e) => `.${e}`).join(',');

  const validarEAdicionar = (lista: FileList | File[]) => {
    setErroFormato(null);
    const arquivosValidos: File[] = [];
    let encontrouInvalido = false;

    Array.from(lista).forEach((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!acceptedExtensions.includes(ext)) {
        encontrouInvalido = true;
        return;
      }
      if (file.size === 0) {
        encontrouInvalido = true;
        return;
      }
      arquivosValidos.push(file);
    });

    if (encontrouInvalido) {
      setErroFormato(`Este tipo de arquivo não é permitido. Selecione um arquivo ${extensoesTexto}.`);
    }

    if (arquivosValidos.length > 0) {
      const novaLista = multiple ? [...selecionados, ...arquivosValidos] : [arquivosValidos[0]];
      setSelecionados(novaLista);
      onFilesSelected(novaLista);
    }
  };

  const removerArquivo = (index: number) => {
    const novaLista = selecionados.filter((_, i) => i !== index);
    setSelecionados(novaLista);
    onFilesSelected(novaLista);
  };

  return (
    <div>
      <div
        className={`audit-uploader-dropzone ${arrastando ? 'audit-uploader-dropzone-ativo' : ''} ${disabled ? 'audit-uploader-dropzone-disabled' : ''}`}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={(e) => {
          e.preventDefault();
          setArrastando(false);
          if (!disabled && e.dataTransfer.files.length > 0) validarEAdicionar(e.dataTransfer.files);
        }}
      >
        <i className='bi bi-cloud-arrow-up audit-uploader-icon' />
        <div className='audit-uploader-texto-principal'>
          Arraste o arquivo aqui ou <span className='audit-uploader-link'>clique para selecionar</span>
        </div>
        <div className='audit-uploader-texto-secundario'>
          <strong>Formatos aceitos: {extensoesTexto}.</strong>
        </div>
        <input
          ref={inputRef}
          type='file'
          accept={acceptAttr}
          multiple={multiple}
          disabled={disabled}
          hidden
          onChange={(e) => e.target.files && validarEAdicionar(e.target.files)}
        />
      </div>

      {erroFormato && (
        <div className='mt-3'>
          <AuditMessage type='ERROR' title='Formato não permitido' message={erroFormato} />
        </div>
      )}

      {selecionados.length > 0 && (
        <div className='audit-uploader-lista mt-3'>
          {selecionados.map((file, index) => (
            <div key={`${file.name}-${index}`} className='audit-uploader-item'>
              <i className='bi bi-file-earmark-text audit-uploader-item-icon' />
              <div className='audit-uploader-item-info'>
                <div className='audit-uploader-item-nome'>{file.name}</div>
                <div className='audit-uploader-item-meta'>
                  {(file.name.split('.').pop() || '').toUpperCase()} · {formatarTamanho(file.size)}
                </div>
              </div>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light-danger'
                title='Remover'
                onClick={(e) => {
                  e.stopPropagation();
                  removerArquivo(index);
                }}
              >
                <i className='bi bi-trash' />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditFileUploader;
